import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { supportService } from '../../services/support.service';
import { SupportTicket } from '../../types';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { Loading } from '../../components/ui/Loading';

export const SupportPage = () => {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    priority: 'MEDIUM'
  });

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const data = await supportService.getSupportTickets();
      setTickets(data);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await supportService.createSupportTicket(formData);
      toast.success('Support ticket created successfully');
      setShowModal(false);
      setFormData({
        subject: '',
        description: '',
        priority: 'MEDIUM'
      });
      loadTickets();
    } catch (error: any) {
      toast.error(error.message || 'Failed to create support ticket');
    }
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'OPEN':
        return 'bg-blue-100 text-blue-800';
      case 'IN_PROGRESS':
        return 'bg-yellow-100 text-yellow-800';
      case 'RESOLVED':
        return 'bg-green-100 text-green-800';
      case 'CLOSED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return 'bg-red-100 text-red-800';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800';
      case 'LOW':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Support Center</h1>
          <p className="text-gray-600">Get help and support</p>
        </div>
        <Button onClick={() => setShowModal(true)}>Create Ticket</Button>
      </div>

      {/* Emergency Contact */}
      <div className="bg-red-50 rounded-lg p-6 mb-6 border-l-4 border-red-500">
        <h3 className="font-semibold text-red-900 mb-2">🚨 Emergency Contact</h3>
        <p className="text-red-800 mb-2">For medical emergencies, call our 24×7 helpline:</p>
        <p className="text-2xl font-bold text-red-900">1800-XXX-XXXX</p>
      </div>

      {/* Service Coordinator */}
      <div className="bg-blue-50 rounded-lg p-6 mb-6 border-l-4 border-blue-500">
        <h3 className="font-semibold text-blue-900 mb-2">📞 Service Coordinator</h3>
        <p className="text-blue-800 mb-2">Need assistance? Contact your dedicated coordinator:</p>
        <p className="text-lg font-semibold text-blue-900">coordinator@hospital.com</p>
        <p className="text-lg font-semibold text-blue-900">+91-XXXX-XXXXXX</p>
      </div>

      {/* Support Tickets */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">My Support Tickets</h2>
        <div className="space-y-4">
          {tickets.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              No support tickets yet. Click "Create Ticket" to get help.
            </p>
          ) : (
            tickets.map((ticket) => (
              <div key={ticket.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-semibold">{ticket.subject}</h3>
                    <p className="text-sm text-gray-500">Created on {formatDate(ticket.createdAt)}</p>
                  </div>
                  <div className="flex gap-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(ticket.status)}`}>
                      {ticket.status}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(ticket.priority)}`}>
                      {ticket.priority}
                    </span>
                  </div>
                </div>
                <p className="text-gray-700 text-sm">{ticket.description}</p>
                {ticket.resolution && (
                  <div className="mt-3 bg-green-50 rounded p-3">
                    <p className="text-sm text-green-900">
                      <strong>Resolution:</strong> {ticket.resolution}
                    </p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Create Support Ticket"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Subject"
            value={formData.subject}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              rows={4}
              required
            />
          </div>
          <Select
            label="Priority"
            value={formData.priority}
            onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
            options={[
              { value: 'LOW', label: 'Low' },
              { value: 'MEDIUM', label: 'Medium' },
              { value: 'HIGH', label: 'High' }
            ]}
          />
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button type="submit">Create Ticket</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
